import { Button, FormField, Spacer, Text, TextInput } from '@loomhq/lens';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';

import {
  AUTH0,
  GOOGLE,
  NONE,
  SLACK,
  USERNAME,
  WINDOWS,
} from 'constants/authTypes';
import { ERROR } from 'constants/error-severities';
import { ENTER } from 'constants/keyCodes';
import { ANON_RECORD_A_REPLY } from 'constants/onboarding';
import { selectPlayerTime } from 'selectors/videoPlayer';
import * as analytics from 'utilities/analytics';
import { getPrimaryAuthTypeForEmail } from 'utilities/auth';
import * as logger from 'utilities/loggerx';
import { secondsToVideoTS } from 'utilities/time';

import { showErrorBar } from '../../../../actions/errorBar';
import { updateLoginEmailError } from '../../../../actions/login';
import { EMAIL_LOGIN_FAILED } from '../../../../constants/events';
import { email as validateEmail } from '../../../../../../shared/validate';

import { MaskedInput } from 'components/signup/email-signup-form/masked-input';

import styles from './styles.module.less';

const INVALID_EMAIL_MSG = 'Enter a valid email (jane@example.com)';

const onCompletedHandler = (data, dispatch, setAuthType) => {
  try {
    const { response } = data || {};

    const resultType = response.__typename;

    if (resultType !== 'GetPrimaryAuthTypePayload') {
      throw new Error(data.response.message);
    }

    setAuthType(response.authType);
  } catch (err) {
    analytics.track(EMAIL_LOGIN_FAILED, {
      reason: 'unknown',
      source: ANON_RECORD_A_REPLY,
    });

    logger.error(err, 'Unable to identify domain for login');
    dispatch(
      showErrorBar(
        ERROR,
        'This account cannot log in at this time. Please try again later.'
      )
    );
  }
};

export function SmartSigninRouting(props) {
  const {
    email,
    setEmail,
    password,
    setPassword,
    errorStr,
    signInHandler,
    nextClickHandler,
  } = props;

  const dispatch = useDispatch();
  const [bouncedEmail, setBouncedEmail] = useState(email);
  const [authType, setAuthType] = useState(null);
  const [emailDebounced] = useDebounce(bouncedEmail, 500);
  const [checkLoading, setCheckLoading] = useState(false);
  const videoCurrentTime = useSelector(selectPlayerTime);

  let canContinue =
    authType !== NONE && validateEmail(emailDebounced) && !checkLoading;

  canContinue =
    canContinue && (authType === USERNAME ? password.length > 0 : true);

  useEffect(() => {
    const getAuthType = async () => {
      if (validateEmail(emailDebounced)) {
        setCheckLoading(true);
        const { data } = await getPrimaryAuthTypeForEmail(emailDebounced);

        onCompletedHandler(data, dispatch, setAuthType);
        setEmail(emailDebounced);
        setCheckLoading(false);
      } else {
        if (emailDebounced.length) {
          dispatch(updateLoginEmailError(INVALID_EMAIL_MSG));
        }

        setAuthType(null);
      }
    };

    getAuthType();
  }, [dispatch, emailDebounced, setEmail]);

  const currentTime =
    videoCurrentTime > 0
      ? ` at ${secondsToVideoTS(Math.round(videoCurrentTime))}`
      : '';

  const label =
    {
      [AUTH0]: 'Continue with SSO',
      [GOOGLE]: 'Continue with Google',
      [SLACK]: 'Continue with Slack',
      [WINDOWS]: 'Continue with Outlook',
    }[authType] || 'Sign in and comment' + currentTime;

  return (
    <>
      <FormField className={styles.smartSigninFormField} labelFor="email">
        <TextInput
          id="email"
          placeholder="name@company.com"
          value={bouncedEmail}
          onChange={e => {
            setBouncedEmail(e.target.value);
          }}
          type="email"
          onKeyDown={e => {
            if (e.keyCode === ENTER && canContinue) {
              setEmail(emailDebounced);
              signInHandler(authType);
            }
          }}
        />

        {authType === NONE && (
          <Text color="red" isInline>
            We do not recognize this email.
          </Text>
        )}
      </FormField>

      {authType === USERNAME && (
        <>
          <Spacer top={1} />
          <FormField
            className="width:full"
            label="Password"
            labelFor="password"
          >
            <MaskedInput
              id="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => {
                if (e.keyCode === ENTER) {
                  setEmail(emailDebounced);
                  signInHandler(authType);
                }
              }}
            />

            {errorStr && (
              <>
                <Text color="red" isInline>
                  Your login information was incorrect.
                </Text>{' '}
                <Text
                  onClick={nextClickHandler}
                  color="blurple"
                  fontWeight="medium"
                  isInline
                >
                  {/* previously '#' was used but it adds weird side effects like preventing the browser from redirecting
                  after users log in. according to stackoverflow, this is the best option to avoid any side effects
                  https://stackoverflow.com/questions/5637969/is-an-empty-href-valid */}
                  <a href="javascript:;">Reset password</a>
                </Text>
              </>
            )}
          </FormField>
        </>
      )}
      <Spacer top={1} />
      <Button
        // id used as cypress testing hook
        id="email-signup-button"
        size={'medium'}
        variant="primary"
        hasFullWidth={true}
        disabled={!canContinue}
        onClick={() => {
          setEmail(emailDebounced);
          signInHandler(authType);
        }}
      >
        {label}
      </Button>
    </>
  );
}
